import React, { useEffect, useState } from "react";
import { Table, Form, Button, Badge } from "react-bootstrap";

function rowKey(row) {
  return `${row.form_field_id}:${row.repeating_row_id || 0}`;
}

// Phase 3C, §13/§21/§30. Editable manual-scoring form used by
// FormReviewQueuePage.jsx. `scores`: GET /api/form-submissions/:id/manual-scores
// (already filtered server-side to allow_manual_score fields only).
// `onSave(payload)` receives exactly the PUT .../manual-scores body shape —
// the backend remains the sole authority on limits/validation; this
// component only shapes the request.
export default function ManualScoringPanel({ scores, onSave, saving, readOnly }) {
  const [entries, setEntries] = useState({});

  useEffect(() => {
    const next = {};
    (scores || []).forEach((row) => {
      next[rowKey(row)] = { score: row.raw_score ?? "", remarks: row.reviewer_remarks || "" };
    });
    setEntries(next);
  }, [scores]);

  if (!scores || !scores.length) {
    return <div className="text-muted small">No fields in this submission require a manual score.</div>;
  }

  const updateEntry = (row, patch) => {
    setEntries((prev) => ({ ...prev, [rowKey(row)]: { ...prev[rowKey(row)], ...patch } }));
  };

  const handleSave = () => {
    const payload = {
      scores: (scores || [])
        .filter((row) => entries[rowKey(row)]?.score !== "" && entries[rowKey(row)]?.score != null)
        .map((row) => ({
          form_field_id: row.form_field_id,
          repeating_row_id: row.repeating_row_id || null,
          score: Number(entries[rowKey(row)].score),
          remarks: entries[rowKey(row)].remarks || null,
        })),
    };
    if (!payload.scores.length) return;
    onSave(payload);
  };

  return (
    <div>
      <Table size="sm" responsive>
        <thead>
          <tr>
            <th>Field</th>
            <th>Row</th>
            <th style={{ width: 110 }}>Score</th>
            <th>Max</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {scores.map((row) => {
            const entry = entries[rowKey(row)] || { score: "", remarks: "" };
            return (
              <tr key={rowKey(row)}>
                <td>
                  {row.label}
                  {!!row.manual_score_required && <Badge bg="warning" text="dark" className="ms-1">Required</Badge>}
                </td>
                <td>{row.row_number != null ? `Row ${row.row_number}` : "—"}</td>
                <td>
                  <Form.Control
                    size="sm" type="number" min={0} max={row.maximum_score ?? undefined} disabled={readOnly || saving}
                    value={entry.score} onChange={(e) => updateEntry(row, { score: e.target.value })}
                  />
                </td>
                <td>{row.maximum_score ?? "—"}</td>
                <td>
                  <Form.Control
                    size="sm" placeholder="Remarks" disabled={readOnly || saving}
                    value={entry.remarks} onChange={(e) => updateEntry(row, { remarks: e.target.value })}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {!readOnly && (
        <Button size="sm" variant="primary" disabled={saving} onClick={handleSave}>
          {saving ? "Saving..." : "Save Manual Scores"}
        </Button>
      )}
    </div>
  );
}
